import './AcceptTerms.css';
import '../common-style.css';

import { useContext, useState } from 'react';
import { signUp, googleLogin, appleLogin } from '../../connector/auth';
import { AppState } from '../../context/general';
import { useProgressModal } from 't-lib-ui/structure';
import { redirect, RouterContext, Link } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal } from 't-lib-ui/structure';
import { ErrorNote, TextButton, Checkbox } from 't-lib-ui/widgets';
import { FetchError } from 't-lib-ui/connector';
import DownreadLogo from '../../components/DownreadLogo';
import TurnstileWidget, { isTurnstileConfigured } from '../../components/TurnstileWidget';
import { getPendingSignup, setPendingSignup } from './pending-signup';
import { markAnonMigrationIfNeeded } from '../../connector/anon-migration';
import { finishConfirmedLogin } from './finish-confirm';

// The ToS-acceptance + Turnstile step of the signup flow. The Signup form validates locally and
// hands its data over via pending-signup (no server interaction); the account is only created
// HERE, once the user agreed to the ToS and passed the challenge — so bots can neither create
// accounts nor trigger confirmation emails. The confirmation email is sent by this signup call.
const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const appState = useContext(AppState);
  useFlowFormGlobal();

  const [presentLoading, dismissLoading] = useProgressModal();

  const [agreed, setAgreed] = useState(false);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [resetSignal, setResetSignal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'ok'|'connection'|'turnstile'|'misconfigured'|'dupe'|'external'|'InvitationInvalid'|'InvitationUsed'|'NoCapacity'>('ok');

  const pending = getPendingSignup();
  // Google and Apple signups skip the Turnstile: the provider's sign-in itself is the bot barrier,
  // and the server never verifies a token on /auth/google or /auth/apple. Only the ToS checkbox is
  // required.
  const provider = pending?.googleCredential ? 'Google' : pending?.appleCredential ? 'Apple' : undefined;
  const isExternal = !!provider;

  const statusErrorMessages = {
    connection: <>Failed to connect to server. Please try again later.</>,
    turnstile: <>Verification failed. Please try again.</>,
    misconfigured: <>Verification is unavailable: this build has no Turnstile sitekey configured.</>,
    dupe: <>This email is already registered. <Link to={`/login?email=${encodeURIComponent(pending?.email || '')}`}>Log in instead?</Link></>,
    external: <>{provider} sign-in failed or expired. Please <Link to='/signup'>start again</Link>.</>,
    InvitationInvalid: <>This invitation link is no longer valid.</>,
    InvitationUsed: <>This invitation has already been fully used.</>,
    NoCapacity: <>We can't accept new sign-ups at the moment. Please try again later.</>,
  };

  if (appState.state.authData?.status == 'confirmed')
    return redirect(routerCtx, '/a');
  // No pending signup (direct visit or page reload) — the form data lives in memory only.
  if (!pending)
    return redirect(routerCtx, '/signup');

  async function continueClicked() {
    if (!agreed || busy || !pending || (!isExternal && !token)) return;
    setBusy(true);
    setStatus('ok');
    presentLoading({message: 'Creating your account...', timeout: 10000});
    try {
      markAnonMigrationIfNeeded();
      if (isExternal) {
        const {authData, user} = pending.googleCredential
          ? await googleLogin(pending.googleCredential, pending.invitationCode, true)
          : await appleLogin(pending.appleCredential!, pending.invitationCode, true);
        setPendingSignup(undefined);
        if (!user)
          await finishConfirmedLogin(appState, authData);
        else
          appState.setLoggedIn(authData, user);
        dismissLoading();
        return redirect(routerCtx, '/a');
      }
      const authData = await signUp(pending.email!, pending.password, 'user', undefined, undefined, pending.tag,
        pending.invitationCode, undefined, undefined, undefined, token);
      setPendingSignup(undefined);
      dismissLoading();
      appState.setUnconfirmed(authData);
      return redirect(routerCtx, '/confirm-email');
    }
    catch (e) {
      console.log('Signup error: ' + e);
      dismissLoading();
      setBusy(false);
      const type = e instanceof FetchError ? e.type : 'Connection';
      if (type == 'TurnstileFailed') {
        // Without a sitekey the widget sent a dev-bypass token; a retry can never succeed.
        setStatus(isTurnstileConfigured() ? 'turnstile' : 'misconfigured');
        setResetSignal(s => s + 1);
      }
      else if (type == 'UserExists')
        setStatus('dupe');
      else if (isExternal && type == 'LoginFailed')
        setStatus('external');
      else if (type == 'InvitationInvalid' || type == 'InvitationUsed' || type == 'NoCapacity')
        setStatus(type);
      else
        setStatus('connection');
    }
  }

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard acceptTermsCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /></a>
          </div>
          <div className="authIntro">Almost there! Please accept our terms below.</div>

          <div className="acceptTermsAgree">
            <Checkbox checked={agreed} onToggle={setAgreed} />
            <span className="acceptTermsAgreeText" onClick={() => setAgreed(a => !a)}>
              I agree to the <a href="/terms" target="_blank" rel="noopener">Terms of Service</a> and
              the <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
            </span>
          </div>
          {!isExternal && (
            <div className="acceptTermsTurnstile">
              <TurnstileWidget action="signup-tos" onToken={setToken} resetSignal={resetSignal} />
            </div>
          )}
          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />
          <TextButton label="Create Account" onClick={continueClicked} className="next acceptTermsContinue" type="major"
            busy={busy} enabled={agreed && (isExternal || token) && !busy ? 'enabled' : 'disabled'} />

          {/* Acceptance must be voluntary — always offer a way out that doesn't imply agreement. */}
          <div className="authLink acceptTermsCancel">
            <Link to="/signup">Cancel</Link>
          </div>
        </div>
      </div>
    </FlowForm>
  );
};

export default Page;
